'use client';

import { useRouter } from 'next/navigation';
import React, { useState, useMemo } from 'react';
import { useSession, signIn, signOut } from 'next-auth/react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

// Check if running on localhost for dev bypass
const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

export default function Page() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const [roomName, setRoomName] = useState('');

  // On localhost, create a mock session for testing
  const effectiveSession = useMemo(() => {
    if (isLocalhost && !session) {
      return { user: { email: 'dev@localhost' } };
    }
    return session;
  }, [session]);

  const startMeeting = () => {
    if (!effectiveSession) {
      // Should not happen since button is disabled, but safety check
      signIn('google');
      return;
    }
    const finalRoomName = roomName.trim() || generateRoomId();
    if (e2ee) {
      router.push(`/rooms/${finalRoomName}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/rooms/${finalRoomName}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && effectiveSession) {
      startMeeting();
    }
  };

  const isLoading = status === 'loading';

  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 700,
            color: '#fff',
            margin: '0 0 0.5rem 0',
            textAlign: 'center'
          }}>
            Fractal Meet
          </h1>
          <h2>
            Video conferencing with AI-powered participation.
          </h2>
        </div>

        <div className={styles.tabContainer}>
          <div className={styles.tabContent}>
            {/* Auth status section */}
            {effectiveSession ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5rem 0.75rem',
                background: isLocalhost && !session ? 'rgba(251, 191, 36, 0.1)' : 'rgba(74, 222, 128, 0.1)',
                border: `1px solid ${isLocalhost && !session ? 'rgba(251, 191, 36, 0.3)' : 'rgba(74, 222, 128, 0.3)'}`,
                borderRadius: '0.5rem',
                marginBottom: '0.5rem',
                fontSize: '0.875rem'
              }}>
                <span style={{ color: isLocalhost && !session ? 'rgba(251, 191, 36, 0.9)' : 'rgba(74, 222, 128, 0.9)' }}>
                  {isLocalhost && !session ? 'Dev Mode (localhost)' : effectiveSession.user?.email}
                </span>
                {session && (
                  <button
                    onClick={() => signOut()}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem'
                    }}
                  >
                    Sign out
                  </button>
                )}
              </div>
            ) : (
              <div style={{
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem',
                marginBottom: '0.5rem',
                fontSize: '0.875rem',
                color: 'rgba(255, 255, 255, 0.7)',
                textAlign: 'center'
              }}>
                Sign in with your @fractallabs.dev account to start a meeting
              </div>
            )}

            <input
              id="roomName"
              type="text"
              placeholder="Meeting name (optional)"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!effectiveSession}
              style={{
                padding: '0.75rem 1rem',
                fontSize: '1rem',
                borderRadius: '0.5rem',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                opacity: effectiveSession ? 1 : 0.5
              }}
            />

            {effectiveSession ? (
              <button
                className="lk-button"
                onClick={startMeeting}
                style={{
                  marginTop: '0.5rem',
                  padding: '0.875rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                Start Meeting
              </button>
            ) : (
              <button
                className="lk-button"
                onClick={() => signIn('google')}
                disabled={isLoading}
                style={{
                  marginTop: '0.5rem',
                  padding: '0.875rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {isLoading ? 'Loading...' : 'Sign in with Google'}
              </button>
            )}

            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '0.75rem',
              alignItems: 'center',
              marginTop: '0.5rem',
              opacity: effectiveSession ? 1 : 0.5
            }}>
              <input
                id="use-e2ee"
                type="checkbox"
                checked={e2ee}
                onChange={(ev) => setE2ee(ev.target.checked)}
                disabled={!effectiveSession}
              />
              <label htmlFor="use-e2ee" style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                Enable end-to-end encryption
              </label>
            </div>

            {e2ee && effectiveSession && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                marginTop: '0.5rem'
              }}>
                <label htmlFor="passphrase" style={{ fontSize: '0.875rem', opacity: 0.7 }}>
                  Encryption passphrase
                </label>
                <input
                  id="passphrase"
                  type="password"
                  value={sharedPassphrase}
                  onChange={(ev) => setSharedPassphrase(ev.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.875rem',
                    borderRadius: '0.375rem',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#fff'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </main>

      <footer data-lk-theme="default">
        Built by{' '}
        <a href="https://fractallabs.dev" rel="noopener">
          Fractal Labs
        </a>
      </footer>
    </>
  );
}
